import { useCallback, useMemo } from "react";
import { mdiDeleteOutline, mdiEyeOutline, mdiPencilOutline } from "@mdi/js";
import Icon from "@mdi/react";
import clsx from "clsx";

import { FILE_TYPE_ICON_LIST } from "constants/loads";
import { formatFileSize } from "helpers/formatFileSize";
import { useUserFeature } from "hooks/useUserFeature";
import styles from "./style.module.scss";
import { showErrorMessage } from "helpers/toastMessage";
import { useModal } from "context/ModalContext";
import { IFRAttachment } from "models/HaulPay";
import Select from "../Select";
import { VISIBILITY_LIST } from "constants/haulpay-broker";

interface IProps {
  file: IFRAttachment;
  hideViewButton?: boolean;
  deleteFile?: (id: string) => void;
  setFile?: (value: IFRAttachment[]) => void;
}

export default function AttachmentItem({
  file,
  hideViewButton = false,
  deleteFile,
  setFile
}: Readonly<IProps>) {
  const { isBroker } = useUserFeature();
  const { openModal, closeModal } = useModal();
  const FileType = useMemo(
    () => FILE_TYPE_ICON_LIST.find((item) => item.type === file.type) ?? FILE_TYPE_ICON_LIST[2],
    [file]
  );

  const previewAttachment = useCallback(() => {
    const newWindow = window.open(file?.downloadUrl, "_blank");
    if (newWindow) {
      newWindow.focus();
    } else {
      showErrorMessage("Failed to open the window. It might have been blocked by the browser.");
    }
  }, [file]);

  const handleConfirmDeleteAttachment = useCallback(
    (id: string) =>
      openModal("warning", {
        message: "Are you sure you want to delete this attachment?",
        onConfirm: () => {
          deleteFile && deleteFile(id);
          closeModal("warning");
        },
        onClose: () => closeModal("warning")
      }),
    [deleteFile]
  );

  const editAttachment = () => {
    if (setFile) {
      setFile([file]);
    }
  };

  return (
    <div className={styles.uploadedFileCard}>
      <div className={clsx(styles.cardBody, !isBroker && "flex-1")}>
        {FileType.icon({
          className: "flex-none text-primary size-9"
        })}
        <div className={styles.description}>
          <div className={styles.fileInfo}>
            <span className="font-medium break-all">{file.filename}</span>
            <span className="text-left text-placeholder">Document Type</span>
          </div>
          <div className={styles.fileInfo}>
            <span className="text-left text-placeholder">{formatFileSize(file?.size ?? 0)}</span>
            <span>{file.category}</span>
          </div>
        </div>
      </div>
      {!hideViewButton ? (
        <div className={styles.cardFooter}>
          <span className={clsx(!isBroker && "!hidden", "text-placeholder")}>Visibility</span>
          <Select
            options={VISIBILITY_LIST}
            value={VISIBILITY_LIST.find((data) => data.value === file?.visibleTo.join(","))}
            className={styles.list}
            btnClass={clsx(
              !isBroker && "!hidden",
              "mobile:!p-1.5 !p-2 mobile:!pr-10 !pr-14 !rounded !bg-transparent !border-tertiary mobile:!w-24 mobile:!text-xs"
            )}
            disabled={!isBroker}
          />
          <button type="button" className={styles.iconButton} onClick={previewAttachment}>
            <Icon path={mdiEyeOutline} />
          </button>
          <button type="button" className={styles.iconButton} onClick={editAttachment}>
            <Icon path={mdiPencilOutline} />
          </button>
          <button
            type="button"
            className={styles.iconButton}
            onClick={() => file?.id && handleConfirmDeleteAttachment(file?.id)}>
            <Icon path={mdiDeleteOutline} />
          </button>
        </div>
      ) : null}
    </div>
  );
}
